import React, { useEffect, useMemo, useState } from 'react';
import { SemanticControlPlaneVisual } from './InteractiveAnalyticsVisuals';

import { INTERACTIVE_ANALYTICS } from '../../../../content/staticAssets';

const BASE = INTERACTIVE_ANALYTICS.contextGapBase;

const TWO_FRAME_SET = [`${BASE}/context-gap-start.png`, `${BASE}/context-gap-final.png`];

const SEQUENCE_FRAMES = [
  `${BASE}/sequence/frame-01.png`,
  `${BASE}/sequence/frame-02.png`,
  `${BASE}/sequence/frame-03.png`,
  `${BASE}/sequence/frame-04.png`,
];

function usePrefersReducedMotion() {
  const [reduced, setReduced] = useState(false);

  useEffect(() => {
    const mq = window.matchMedia('(prefers-reduced-motion: reduce)');
    setReduced(mq.matches);
    const onChange = () => setReduced(mq.matches);
    mq.addEventListener('change', onChange);
    return () => mq.removeEventListener('change', onChange);
  }, []);

  return reduced;
}

function useReplayOnInView(threshold = 0.25) {
  const ref = React.useRef(null);
  const [playKey, setPlayKey] = useState(0);
  const inView = React.useRef(false);

  useEffect(() => {
    const el = ref.current;
    if (!el) return undefined;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting && !inView.current) {
          inView.current = true;
          setPlayKey((k) => k + 1);
        } else if (!entry.isIntersecting && inView.current) {
          inView.current = false;
        }
      },
      { threshold, rootMargin: '0px 0px -8% 0px' }
    );

    observer.observe(el);
    return () => observer.disconnect();
  }, [threshold]);

  return { ref, playKey, active: playKey > 0 };
}

function useImagePreload(sources) {
  const [states, setStates] = useState(() => sources.map(() => 'pending'));

  useEffect(() => {
    setStates(sources.map(() => 'pending'));

    const cleanups = sources.map((src, index) => {
      const img = new Image();
      const mark = (status) => {
        setStates((prev) => {
          const next = [...prev];
          next[index] = status;
          return next;
        });
      };

      img.onload = () => mark('ok');
      img.onerror = () => mark('error');
      img.src = src;

      return () => {
        img.onload = null;
        img.onerror = null;
      };
    });

    return () => cleanups.forEach((cleanup) => cleanup());
  }, [sources]);

  const readyCount = states.filter((s) => s === 'ok').length;
  const assetsReady = readyCount === sources.length;
  const assetsSettled = states.every((s) => s !== 'pending');
  const assetsFailed = assetsSettled && !assetsReady;
  const firstReadyIndex = states.findIndex((s) => s === 'ok');

  return { states, assetsReady, assetsFailed, firstReadyIndex };
}

/**
 * Context gap storyboard: PNG frames cross-fade when scrolled into view.
 *
 * `variant="sequence"` (default): four-frame story: gap → converge → semantic hub → connected.
 * `variant="crossfade"`: two-frame transparent PNG cross-fade (earlier storyboard).
 */
export default function ContextGapCrossfade({
  variant = 'sequence',
  alt = 'Business context and AI agents separated by a gap, then connected through a central semantic layer',
}) {
  const reducedMotion = usePrefersReducedMotion();
  const { ref, playKey, active } = useReplayOnInView();
  const frames = useMemo(() => (variant === 'crossfade' ? TWO_FRAME_SET : SEQUENCE_FRAMES), [variant]);
  const { assetsReady, assetsFailed, firstReadyIndex } = useImagePreload(frames);

  const isSequence = variant === 'sequence';
  const finalSrc = frames[frames.length - 1];
  const previewSrc = firstReadyIndex >= 0 ? frames[firstReadyIndex] : frames[0];

  if (assetsFailed) {
    return <SemanticControlPlaneVisual />;
  }

  return (
    <div ref={ref} className="cg-wrap w-full">
      <div className="sr-only" role="img" aria-label={alt} />

      {!assetsReady ? (
        <div className="cg-scene relative mx-auto w-full" style={{ aspectRatio: '16 / 9' }} aria-hidden="true">
          {firstReadyIndex >= 0 ? (
            <img className="cg-img-static" src={previewSrc} alt="" draggable="false" />
          ) : (
            <div className="cg-scene cg-scene--loading h-full">
              <SemanticControlPlaneVisual />
            </div>
          )}
        </div>
      ) : reducedMotion ? (
        <div className="cg-scene relative mx-auto w-full" style={{ aspectRatio: '16 / 9' }} aria-hidden="true">
          <img className="cg-img-static" src={finalSrc} alt="" draggable="false" />
        </div>
      ) : (
        <div
          key={playKey}
          className={[
            'cg-scene relative mx-auto w-full',
            isSequence ? 'cg-scene--sequence' : 'cg-scene--crossfade',
            active ? 'cg-scene--playing' : '',
          ]
            .filter(Boolean)
            .join(' ')}
          style={{ aspectRatio: '16 / 9' }}
          aria-hidden="true"
        >
          {frames.map((src, index) => (
            <img
              key={src}
              className={`cg-img cg-frame cg-frame-${index + 1}${index === 0 ? ' cg-img-start' : ''}${index === frames.length - 1 ? ' cg-img-final' : ''}`}
              src={src}
              alt=""
              draggable="false"
            />
          ))}
        </div>
      )}
    </div>
  );
}
